'use client';

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Link,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { MoreVert as MoreVertIcon, Add as AddIcon } from '@mui/icons-material';
import { MoveRight } from 'lucide-react';
import SplitLegModal from 'src/components/apps/LegsComponents/SplitLegModal';
import AddEquipmentModal from 'src/components/apps/LegsComponents/AddEquipmentModal'; // Import AddEquipmentModal
import AddToExistingManifestModal from 'src/components/apps/LegsComponents/AddToExistingManifestModal'; // Import AddToExistingManifestModal
import AssignDriverCarrier from 'src/components/apps/order-components/legs/AssignDriverCarrier'; // Import AssignDriverCarrier
import EditAllocatedRevenue from 'src/components/apps/order-components/legs/EditAllocatedRevenue';
import ChangeTimesModal from 'src/components/apps/LegsComponents/ChangeTimesModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  splitLeg,
  deleteLeg,
  assignCarrierToLeg,
  assignCarrierWithLegToManifest,
  assignEquipmentToLeg,
  updateLegIsLineHaul,
  createManifest,
  addLegToManifest,
  removeLegFromManifest,
  completeLeg,
} from 'src/queries/query';
import { sendAttemptConfirmation } from 'src/queries/query';
import {
  allocateRevenueAcrossLegs,
  updateLeg,
  getCustomer,
} from 'src/queries/queriess';
import { OrderStatus } from 'src/constants/OrderStatus';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Attempt-complete states (a driver "attempt complete" leaves the leg here)
const COMPLETABLE_LEG_STATUSES = ['attempted-pickup', 'attempted-delivery'];

const normalizeLegStatus = (leg) => String(leg?.status || '').toLowerCase();

// A driver attempt-complete adds an "attempted ..." accessorial on the leg
export const hasAttemptedAccessorial = (leg) =>
  (leg?.accessorials || []).some((acc) =>
    String(acc?.name || acc?.title || (typeof acc === 'string' ? acc : ''))
      .toLowerCase()
      .includes('attempted'),
  );

// The persisted field the backend flips true once completion is recorded
export const isLegAttemptedComplete = (leg) =>
  leg?.isAttemptedComplete === true;

// Show "Complete leg" only for an attempt-complete state (status or accessorial),
// and never once the whole order is already delivered
export const canShowCompleteLegButton = (leg, orderStatus) =>
  (COMPLETABLE_LEG_STATUSES.includes(normalizeLegStatus(leg)) ||
    hasAttemptedAccessorial(leg)) &&
  String(orderStatus || '').toLowerCase() !==
    (OrderStatus.DELIVERED || 'delivered');

const LegsTable = ({
  order,
  selectedQuote,
  onOpenCreateManifestModal,
  onOpenAddEquipmentModal,
  onOpenAddToManifestModal,
  onOpenSplitLegModal,
  formatDate,
  refetchOrders,
  onMarkAsPickedUp,
  onMarkAsDelivered,
  isOSND = false,
  // Lock the legs table only when the invoice is paid — a delivered order can
  // still be edited (e.g. undo delivery); invoicepaid is the terminal lock
  isInvoicePaid = false,
}) => {
  const billingPartyUuid = order?.billingParty?.uuid;

  // Order payload may omit this flag; load it from the customer record
  const { data: billingPartyCustomer } = useQuery({
    queryKey: ['customer', billingPartyUuid, 'isPickingFromOurWareHouse'],
    queryFn: async () => {
      const response = await getCustomer(billingPartyUuid);
      return response?.data?.data ?? response?.data ?? null;
    },
    enabled: !!billingPartyUuid,
    staleTime: 60_000,
  });

  const isPickingFromOurWareHouse = !!(
    billingPartyCustomer?.isPickingFromOurWareHouse ??
    order?.billingParty?.isPickingFromOurWareHouse
  );
  // Get all legs to check pickup leg status
  const allLegs = useMemo(() => {
    if (!order) return [];
    if (order.orderStops) {
      return order.orderStops.flatMap((stop) => stop.legs || []);
    } else if (order.legs) {
      return order.legs;
    }
    return [];
  }, [order]);

  // Find the first pickup leg
  const firstPickupLeg = useMemo(() => {
    return allLegs.find((leg) => leg?.type === 'PICKUP');
  }, [allLegs]);

  // Check if the first pickup leg is delivered
  // If there's no pickup leg, we can't allow assignment
  const isPickupLegDelivered = useMemo(() => {
    if (!firstPickupLeg) return false;
    return firstPickupLeg?.status?.toLowerCase() === 'delivered';
  }, [firstPickupLeg]);

  // Whether the invoice is paid — the only state that fully locks the legs table
  const isOrderLocked =
    isInvoicePaid || order?.status?.toLowerCase() === OrderStatus.INVOICEPAID;

  // Check if order status allows manifest/carrier assignment (not "new" or "no-quote")
  const orderStatusAllows = useMemo(() => {
    return !(
      order?.status?.toLowerCase() === 'new' ||
      order?.status?.toLowerCase() === 'no-quote' ||
      order?.status?.toLowerCase() === 'pending'
    );
  }, [order?.status]);

  // Function to check if a specific leg can be assigned driver/carrier or added to manifest
  // Pickup leg can always be assigned (it's the first leg)
  // Other legs can only be assigned if the pickup leg is delivered
  const canLegBeAssigned = useCallback(
    (leg) => {
      if (!orderStatusAllows) return false;

      // If this is the pickup leg itself, allow assignment
      if (leg?.uuid === firstPickupLeg?.uuid) {
        return true;
      }

      // For all other legs, pickup leg must be delivered
      return isPickupLegDelivered;
    },
    [orderStatusAllows, firstPickupLeg?.uuid, isPickupLegDelivered],
  );

  // Traditional Line Haul toggle (assign carrier modal)
  const canSetLineHaul = useCallback((leg) => {
    return !!leg?.isAssignAsLineHaul;
  }, []);

  // Warehouse picking: assign leg directly to a Line Haul manifest
  const canAssignWarehouseLineHaul = useCallback(
    (leg) => {
      if (!isPickingFromOurWareHouse) return false;
      const manifest = leg?.manifest || leg?.manifestStop?.manifest;
      const hasManifest = !!(
        manifest?.uuid ||
        manifest?.manifestName ||
        manifest?.manifestNumber ||
        leg?.manifestId
      );
      return !hasManifest;
    },
    [isPickingFromOurWareHouse],
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSplitLegModalOpen, setIsSplitLegModalOpen] = useState(false);
  const [selectedLegToSplit, setSelectedLegToSplit] = useState(null);
  const [isAssignCarrierModalOpen, setIsAssignCarrierModalOpen] =
    useState(false);
  const [selectedLegForAssignment, setSelectedLegForAssignment] =
    useState(null);
  const [isAddEquipmentModalOpen, setIsAddEquipmentModalOpen] = useState(false); // New state for AddEquipmentModal
  const [selectedLegForEquipment, setSelectedLegForEquipment] = useState(null); // New state for leg to assign equipment to
  const [
    isAddToExistingManifestModalOpen,
    setIsAddToExistingManifestModalOpen,
  ] = useState(false); // New state for AddToExistingManifestModal
  const [selectedLegForManifest, setSelectedLegForManifest] = useState(null); // New state for leg to add to manifest
  const [isAllocatedRevenueModalOpen, setIsAllocatedRevenueModalOpen] =
    useState(false);
  const [isChangeTimesModalOpen, setIsChangeTimesModalOpen] = useState(false);
  const [selectedLegForTimes, setSelectedLegForTimes] = useState(null);
  const [pendingLineHaulLegUuid, setPendingLineHaulLegUuid] = useState(null);
  const [isLineHaulModal, setIsLineHaulModal] = useState(false);
  const [sendingBillingEmailForLeg, setSendingBillingEmailForLeg] = useState(null);
  // Legs the billing email was already sent for (session state → "Resend email" label)
  const [sentEmailLegs, setSentEmailLegs] = useState([]);
  // Leg whose email just went out → transient "Email has been sent" label
  const [justEmailedLeg, setJustEmailedLeg] = useState(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const sendBillingEmailMutation = useMutation({
    mutationFn: ({
      orderUuid,
      orderStopUuid,
      legUuid,
      attemptType,
      accessorials,
    }) =>
      sendAttemptConfirmation(orderUuid, {
        orderStopUuid,
        legUuid,
        attemptType,
        accessorials,
      }),
    onSuccess: (response, variables) => {
      toast.success(
        response?.message || 'Email sent to billing party successfully!',
      );
      if (variables?.legUuid) {
        setSentEmailLegs((prev) => [...new Set([...prev, variables.legUuid])]);
        setJustEmailedLeg(variables.legUuid);
        // Show "Email has been sent" briefly, then switch to "Resend email"
        setTimeout(() => {
          setJustEmailedLeg((current) =>
            current === variables.legUuid ? null : current,
          );
        }, 4000);
      }
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to send email to billing party');
    },
    onSettled: () => setSendingBillingEmailForLeg(null),
  });

  const handleSendBillingEmail = (leg) => {
    const orderUuid = order?.uuid || leg?.orderStop?.order?.uuid;
    const orderStopUuid = leg?.orderStop?.uuid || order?.orderStops?.find((stop) =>
      stop?.legs?.some((stopLeg) => stopLeg?.uuid === leg?.uuid),
    )?.uuid;
    if (!orderUuid || !orderStopUuid) {
      toast.error('Missing order or stop information for this leg.');
      return;
    }
    // Tell the backend what was attempted and why, so the email copy matches
    const status = leg?.status?.toLowerCase() || '';
    const attemptType = status.includes('pickup')
      ? 'pickup'
      : status.includes('delivery')
        ? 'delivery'
        : 'delivery';
    setSendingBillingEmailForLeg(leg.uuid);
    sendBillingEmailMutation.mutate({
      orderUuid,
      orderStopUuid,
      legUuid: leg.uuid,
      attemptType,
      accessorials: leg?.accessorials || [],
    });
  };

  const completeLegMutation = useMutation({
    mutationFn: (legUuid) => completeLeg(legUuid),
    onSuccess: async () => {
      toast.success('Leg completion recorded.');
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (order?.uuid) await queryClient.refetchQueries({ queryKey: ['orders', order.uuid] });
      if (refetchOrders) await Promise.resolve(refetchOrders());
    },
    onError: (error) => toast.error(error?.message || 'Failed to record leg completion'),
  });

  // Mutation for marking leg as picked up
  const markLegAsPickedUpMutation = useMutation({
    mutationFn: (leg) => updateLeg({ uuid: leg.uuid, status: 'picked' }),
    onSuccess: () => {
      toast.success('Leg marked as picked up successfully!');
      queryClient.invalidateQueries(['orders']);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error('Failed to mark leg as picked up:', error);
      toast.error(`Failed to mark leg as picked up: ${error.message}`);
    },
  });

  // Mutation for marking leg as delivered
  const markLegAsDeliveredMutation = useMutation({
    mutationFn: (leg) => updateLeg({ uuid: leg.uuid, status: 'delivered' }),
    onSuccess: () => {
      toast.success('Leg marked as delivered successfully!');
      queryClient.invalidateQueries(['orders']);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error('Failed to mark leg as delivered:', error);
      toast.error(`Failed to mark leg as delivered: ${error.message}`);
    },
  });

  // Mutation for undoing picked status
  const undoPickedMutation = useMutation({
    mutationFn: (leg) => updateLeg({ uuid: leg.uuid, status: 'available' }),
    onSuccess: () => {
      toast.success('Leg status reverted to available!');
      queryClient.invalidateQueries(['orders']);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error('Failed to undo picked status:', error);
      toast.error(`Failed to undo picked status: ${error.message}`);
    },
  });

  // Mutation for undoing delivered status
  const undoDeliveredMutation = useMutation({
    mutationFn: (leg) => updateLeg({ uuid: leg.uuid, status: 'picked' }),
    onSuccess: () => {
      toast.success('Leg status reverted to picked!');
      queryClient.invalidateQueries(['orders']);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error('Failed to undo delivered status:', error);
      toast.error(`Failed to undo delivered status: ${error.message}`);
    },
  });

  // Handler for marking leg as picked up
  const handleMarkAsPickedUp = (leg) => {
    if (leg?.uuid) {
      markLegAsPickedUpMutation.mutate(leg);
    }
  };

  // Handler for marking leg as delivered
  const handleMarkAsDelivered = (leg) => {
    if (leg?.uuid) {
      markLegAsDeliveredMutation.mutate(leg);
    }
  };

  // Handler for undoing picked status
  const handleUndoPicked = (leg) => {
    if (leg?.uuid) {
      undoPickedMutation.mutate(leg);
    }
  };

  // Handler for undoing delivered status
  const handleUndoDelivered = (leg) => {
    if (leg?.uuid) {
      undoDeliveredMutation.mutate(leg);
    }
  };

  const addLegToManifestMutation = useMutation({
    mutationFn: ({ manifestUuid, legUuid }) =>
      addLegToManifest(manifestUuid, legUuid),
    onSuccess: async () => {
      toast.success('Leg added to manifest successfully!');
      setIsAddToExistingManifestModalOpen(false);
      setSelectedLegForManifest(null);

      // Update order status to "in-transit" when leg is added to manifest
      if (selectedLegForManifest?.orderStop?.order?.uuid) {
        try {
          await updateOrderStatus(
            selectedLegForManifest.orderStop.order.uuid,
            OrderStatus.INTRANSIT,
          );
          // Only refetch the specific order that was updated
          await queryClient.refetchQueries([
            'orders',
            selectedLegForManifest.orderStop.order.uuid,
          ]);
        } catch (error) {
          console.error('Failed to update order status:', error);
        }
      }

      await queryClient.refetchQueries(['manifests']);

      // Wait for the general order refetch to complete to show updated details
      if (refetchOrders && typeof refetchOrders === 'function') {
        try {
          // Add a small delay to ensure loader is visible
          await new Promise((resolve) => setTimeout(resolve, 100));
          await refetchOrders();
        } catch (error) {
          console.error('Failed to refetch orders:', error);
        }
      }
    },
    onError: (error) => {
      console.error('Failed to add leg to manifest:', error);
      toast.error(`Failed to add leg to manifest: ${error.message}`);
    },
  });

  const assignEquipmentMutation = useMutation({
    mutationFn: ({ legUuid, equipmentUuids = [] }) =>
      assignEquipmentToLeg(legUuid, equipmentUuids),
    onSuccess: (_data, variables) => {
      const removedOnly =
        (variables?.toRemove?.length || 0) > 0 &&
        (variables?.toAdd?.length || 0) === 0;
      toast.success(
        removedOnly
          ? 'Equipment unassigned from leg successfully!'
          : 'Equipment updated on leg successfully!',
      );
      setIsAddEquipmentModalOpen(false);
      setSelectedLegForEquipment(null);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error('Failed to update equipment on leg:', error);
      toast.error(`Failed to update equipment on leg: ${error.message}`);
    },
  });

  const splitLegMutation = useMutation({
    mutationFn: ({ legUuid, payload }) => splitLeg(legUuid, payload),
    onSuccess: async () => {
      toast.success('Leg split successfully!');
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (refetchOrders) {
        await Promise.resolve(refetchOrders());
      }
      setIsSplitLegModalOpen(false);
      setSelectedLegToSplit(null);
    },
    onError: (error) => {
      console.error('Failed to split leg:', error);
      toast.error(`Failed to split leg: ${error.message}`);
    },
  });

  const lineHaulMutation = useMutation({
    // Accept an object so we can toggle on/off
    mutationFn: ({ legUuid, isLineHaul }) => {
      setPendingLineHaulLegUuid(legUuid);
      return updateLegIsLineHaul(legUuid, isLineHaul);
    },
    onMutate: async ({ legUuid, isLineHaul }) => {
      // Optimistically update the cache
      if (order?.uuid) {
        // Update the order in cache optimistically
        queryClient.setQueryData(['orders', order.uuid], (oldData) => {
          if (!oldData) return oldData;

          const updateLegInOrder = (orderData) => {
            if (orderData.orderStops) {
              return {
                ...orderData,
                orderStops: orderData.orderStops.map((stop) => ({
                  ...stop,
                  legs: stop.legs?.map((leg) =>
                    leg.uuid === legUuid
                      ? {
                          ...leg,
                          isLineHaul,
                          isLinehaul: isLineHaul,
                          lineHaul: isLineHaul,
                          linehaul: isLineHaul,
                          is_line_haul: isLineHaul,
                          line_haul: isLineHaul,
                        }
                      : leg,
                  ),
                })),
              };
            } else if (orderData.legs) {
              return {
                ...orderData,
                legs: orderData.legs.map((leg) =>
                  leg.uuid === legUuid
                    ? {
                        ...leg,
                        isLineHaul,
                        isLinehaul: isLineHaul,
                        lineHaul: isLineHaul,
                        linehaul: isLineHaul,
                        is_line_haul: isLineHaul,
                        line_haul: isLineHaul,
                      }
                    : leg,
                ),
              };
            }
            return orderData;
          };

          return updateLegInOrder(oldData);
        });
      }
    },
    onSuccess: (_data, variables) => {
      if (variables?.isLineHaul) {
        toast.success('Leg marked as Line Haul.');
      } else {
        toast.success('Line Haul removed from leg.');
      }
      // Invalidate only the specific order query in background - optimistic update already handled UI
      if (order?.uuid) {
        queryClient.invalidateQueries(['orders', order.uuid], { exact: true });
      }
      setPendingLineHaulLegUuid(null);
    },
    onError: (error, variables, context) => {
      console.error('Failed to update Line Haul status:', error);
      toast.error(`Failed to update Line Haul status: ${error.message}`);
      // Rollback optimistic update on error
      if (order?.uuid) {
        queryClient.invalidateQueries(['orders', order.uuid]);
      }
      setPendingLineHaulLegUuid(null);
    },
  });

  const assignToLineHaulMutation = useMutation({
    mutationFn: async (leg) => {
      setPendingLineHaulLegUuid(leg.uuid);
      await updateLegIsLineHaul(leg.uuid, true);
      return createManifest(leg.uuid, false, true);
    },
    onSuccess: (manifest) => {
      toast.success('Leg assigned to Line Haul manifest');
      if (order?.uuid) {
        queryClient.invalidateQueries(['orders', order.uuid]);
      }
      setPendingLineHaulLegUuid(null);
      const manifestUuid = manifest?.uuid;
      if (manifestUuid) {
        navigate(`/manifest/blank/${manifestUuid}`);
      }
    },
    onError: (error) => {
      setPendingLineHaulLegUuid(null);
      toast.error(
        error?.message || 'Failed to assign leg to Line Haul manifest',
      );
    },
  });

  const deleteLegMutation = useMutation({
    mutationFn: (legUuid) => deleteLeg([legUuid]),
    onSuccess: () => {
      toast.success('Leg deleted successfully!');
      queryClient.invalidateQueries(['orders']);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error('Failed to delete leg:', error);
      toast.error(`Failed to delete leg: ${error.message}`);
    },
  });

  const removeLegFromManifestMutation = useMutation({
    mutationFn: async ({ manifestUuid, legUuid }) => {
      console.log('Removing leg from manifest:', { manifestUuid, legUuid });
      const result = await removeLegFromManifest(manifestUuid, legUuid);
      console.log('Remove leg response:', result);
      return result;
    },
    onSuccess: async (data, variables) => {
      console.log('Successfully removed leg from manifest');
      toast.success('Leg removed from manifest successfully!');

      // Find the leg to get order UUID
      let leg = null;
      if (order?.orderStops) {
        leg = order.orderStops
          .flatMap((stop) => stop.legs || [])
          .find((l) => l.uuid === variables.legUuid);
      } else if (order?.legs) {
        leg = order.legs.find((l) => l.uuid === variables.legUuid);
      }
      console.log('Leg found for status update:', leg);

      // Update order status back to "booked" when leg is removed from manifest
      if (leg?.orderStop?.order?.uuid) {
        try {
          console.log(
            'Updating order status to BOOKED for:',
            leg.orderStop.order.uuid,
          );
          await updateOrderStatus(leg.orderStop.order.uuid, OrderStatus.BOOKED);
          console.log('Order status updated successfully');
        } catch (error) {
          console.error('Failed to update order status:', error);
        }
      } else {
        console.error('Could not find order UUID for leg:', variables.legUuid);
      }

      // Refetch only the specific order that was updated
      console.log('Refetching specific order query...');
      if (leg?.orderStop?.order?.uuid) {
        await queryClient.refetchQueries(['orders', leg.orderStop.order.uuid]);
      }
      await queryClient.refetchQueries(['manifests']);
      console.log('Query refetch completed');
    },
    onError: (error) => {
      console.error('Failed to remove leg from manifest:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      toast.error(`Failed to remove leg from manifest: ${error.message}`);
    },
  });

  const saveAllocatedRevenueMutation = useMutation({
    mutationFn: async (payload) => {
      const updates = Array.isArray(payload) ? payload : [];
      if (!order?.uuid) {
        throw new Error('Missing order uuid');
      }

      const allocations = updates
        .filter((u) => u?.uuid)
        .map((u) => ({ legUuid: u.uuid, revenue: u.revenue }));

      return allocateRevenueAcrossLegs({
        orderUuid: order.uuid,
        totalRevenue: allocatedRevenueTotal,
        allocations,
      });
    },
    onSuccess: () => {
      toast.success('Allocated revenue updated successfully!');
      setIsAllocatedRevenueModalOpen(false);
      queryClient.invalidateQueries(['orders']);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error('Failed to update allocated revenue:', error);
      toast.error(`Failed to update allocated revenue: ${error.message}`);
    },
  });

  // Comprehensive loading state for all leg table mutations
  const isAnyLegMutationLoading = useMemo(() => {
    return (
      addLegToManifestMutation.isPending ||
      removeLegFromManifestMutation.isPending ||
      assignEquipmentMutation.isPending ||
      splitLegMutation.isPending ||
      lineHaulMutation.isPending ||
      assignToLineHaulMutation.isPending ||
      deleteLegMutation.isPending ||
      saveAllocatedRevenueMutation.isPending
    );
  }, [
    addLegToManifestMutation.isPending,
    removeLegFromManifestMutation.isPending,
    assignEquipmentMutation.isPending,
    splitLegMutation.isPending,
    lineHaulMutation.isPending,
    assignToLineHaulMutation.isPending,
    deleteLegMutation.isPending,
    saveAllocatedRevenueMutation.isPending,
  ]);

  const isLegLineHaul = (leg) => {
    // Read directly from leg.isLineHaul
    return !!leg?.isLineHaul;
  };

  const handleOpenSplitLegModal = (leg) => {
    setSelectedLegToSplit(leg);
    setIsSplitLegModalOpen(true);
  };

  const handleCloseSplitLegModal = () => {
    setIsSplitLegModalOpen(false);
    setSelectedLegToSplit(null);
  };

  const handleSplitLegSubmit = (addressData) => {
    if (!selectedLegToSplit) {
      console.error('No leg selected for splitting.');
      return;
    }

    const payload = {
      origin: {
        address: addressData.address,
        address2: addressData.address2 || null,
        city: addressData.city,
        state: addressData.state,
        postal: addressData.postal,
        country: addressData.country,
        name: addressData.newCompanyName,
        latitude: Number.parseFloat(addressData.latitude),
        longitude: Number.parseFloat(addressData.longitude),
      },
      revenue: selectedLegToSplit.revenue || 0,
      costs: selectedLegToSplit.costs || 0,
      margin: selectedLegToSplit.margin || 0,
      newCompanyName: addressData.newCompanyName,
    };

    splitLegMutation.mutate({ legUuid: selectedLegToSplit.uuid, payload });
  };

  const sortedLegs = useMemo(() => {
    if (!order) return [];

    let legs = [];
    if (order.orderStops) {
      legs = order.orderStops.flatMap((stop) => stop.legs || []);
    } else if (order.legs) {
      legs = order.legs;
    }

    const parseLegNumberSegments = (legNumber) => {
      if (legNumber === null || legNumber === undefined) return null;

      const s = String(legNumber);
      const legMatch = s.match(/LEG-([0-9]+(?:\.[0-9]+)*)/i);
      const tokenFromLeg = legMatch?.[1];

      const allTokens = s.match(/\d+(?:\.\d+)*/g);
      const tokenFallback = Array.isArray(allTokens)
        ? allTokens[allTokens.length - 1]
        : null;

      const token = tokenFromLeg || tokenFallback;
      if (!token) return null;

      const parts = token.split('.').filter(Boolean);
      if (!parts.length) return null;

      const segments = parts
        .map((p) => Number.parseInt(p, 10))
        .filter((n) => Number.isFinite(n));

      return segments.length ? segments : null;
    };

    const compareSegments = (aSeg, bSeg) => {
      if (!aSeg && !bSeg) return 0;
      if (!aSeg) return 1;
      if (!bSeg) return -1;

      const maxLen = Math.max(aSeg.length, bSeg.length);
      for (let i = 0; i < maxLen; i += 1) {
        const aVal = aSeg[i] ?? 0;
        const bVal = bSeg[i] ?? 0;
        if (aVal !== bVal) return aVal - bVal;
      }
      return 0;
    };

    const filteredLegs = legs
      .filter((leg) => leg)
      .sort((a, b) => {
        const segA = parseLegNumberSegments(a?.legNumber);
        const segB = parseLegNumberSegments(b?.legNumber);
        return compareSegments(segA, segB);
      });
    return filteredLegs;
  }, [order]);

  const handleMenuOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const totals = useMemo(() => {
    const total = Number.parseFloat(selectedQuote?.quoteData?.total || 0);
    const carrierCosts = Number.parseFloat(selectedQuote?.carrierCosts || 0);
    const discountPercent = Number.parseFloat(
      selectedQuote?.totalDiscount || 0,
    );
    const marginBeforeDiscount = total - carrierCosts;
    const discountAmount =
      selectedQuote?.discountType === '$'
        ? selectedQuote?.totalDiscount || 0
        : marginBeforeDiscount * (discountPercent / 100);
    const finalMargin = marginBeforeDiscount - discountAmount;

    return {
      revenue: total,
      costs: carrierCosts,
      discount: discountAmount,
      margin: finalMargin,
      isNegativeMargin: finalMargin < 0,
    };
  }, [selectedQuote]);

  const allocatedRevenueTotal = useMemo(() => {
    if (totals?.revenue > 0) return totals.revenue;
    return sortedLegs.reduce(
      (acc, leg) => acc + (Number(leg?.revenue) || 0),
      0,
    );
  }, [sortedLegs, totals?.revenue]);

  const shouldShowDeleteLeg = (legs, index) => {
    const totalLegs = Array.isArray(legs) ? legs.filter(Boolean).length : 0;
    return totalLegs > 1 && index > 0;
  };

  const handleDeleteLeg = (leg, index) => {
    if (!leg?.uuid) return;
    if (index <= 0) return;
    deleteLegMutation.mutate(leg.uuid);
  };

  const LegActions = ({
    leg,
    index,
    onMarkAsPickedUp,
    onMarkAsDelivered,
    canLegBeAssigned,
    canSetLineHaul,
    canAssignWarehouseLineHaul,
    firstPickupLeg,
  }) => {
    if (isOrderLocked) {
      return null;
    }

    const canAssign = canLegBeAssigned(leg);
    const canSetLineHaulButton = canSetLineHaul(leg);
    const canAssignWarehouseLineHaulButton = canAssignWarehouseLineHaul(leg);
    const isAssigningWarehouseLineHaul =
      assignToLineHaulMutation.isPending &&
      pendingLineHaulLegUuid === leg?.uuid;
    // Check if leg has a manifest assigned (check both possible paths)
    const manifest = leg?.manifest || leg?.manifestStop?.manifest;
    const hasManifest = !!(
      manifest?.manifestName ||
      manifest?.manifestNumber ||
      manifest?.uuid ||
      leg?.manifestId
    );

    return (
      <Box
        sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
      >
        {/* When leg HAS manifest: Show View Manifest, hide Assign/Add buttons */}
        {hasManifest ? (
          <>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() =>
                navigate(
                  `/manifest/blank/${manifest?.uuid || leg?.manifestUuid}`,
                )
              }
            >
              View Manifest
            </Button>
            {leg.type === 'PICKUP' &&
            leg.status?.toLowerCase() !== 'picked' &&
            leg.status?.toLowerCase() !== 'delivered' ? (
              <Button
                size="small"
                color="success"
                onClick={() => handleMarkAsPickedUp?.(leg)}
                disabled={markLegAsPickedUpMutation.isPending}
                startIcon={
                  markLegAsPickedUpMutation.isPending ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Mark as Picked Up
              </Button>
            ) : null}
            {(leg.type === 'DELIVERY' &&
              leg.status?.toLowerCase() !== 'delivered') ||
            leg.status?.toLowerCase() === 'booked' ||
            leg.status?.toLowerCase() === 'in-transit' ||
            leg.status?.toLowerCase() === 'picked' ? (
              <Button
                size="small"
                color="primary"
                onClick={() => handleMarkAsDelivered?.(leg)}
                disabled={markLegAsDeliveredMutation.isPending}
                startIcon={
                  markLegAsDeliveredMutation.isPending ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                Mark as Delivered
              </Button>
            ) : null}
            {leg?.isAttemptedComplete && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleSendBillingEmail(leg)}
                disabled={sendingBillingEmailForLeg === leg?.uuid}
                startIcon={sendingBillingEmailForLeg === leg?.uuid ? <CircularProgress size={14} /> : null}
              >
                {sendingBillingEmailForLeg === leg?.uuid
                  ? 'Sending...'
                  : justEmailedLeg === leg?.uuid
                    ? 'Email has been sent'
                    : sentEmailLegs.includes(leg?.uuid)
                      ? 'Resend email'
                      : 'Send email to billing party'}
              </Button>
            )}
            {canShowCompleteLegButton(leg, order?.status) && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  // Only record completion when the persisted field is not true yet
                  if (isLegAttemptedComplete(leg)) return;
                  completeLegMutation.mutate(leg.uuid);
                }}
                disabled={
                  completeLegMutation.isPending || isLegAttemptedComplete(leg)
                }
                startIcon={completeLegMutation.isPending ? <CircularProgress size={14} /> : null}
              >
                {completeLegMutation.isPending
                  ? 'Completing...'
                  : isLegAttemptedComplete(leg)
                    ? 'Completed'
                    : 'Complete leg'}
              </Button>
            )}
            <Button
              size="small"
              color="primary"
              onClick={() => handleOpenSplitLegModal?.(leg)}
            >
              Split Leg
            </Button>
            {canSetLineHaulButton && (
              <Button
                size="small"
                disabled={pendingLineHaulLegUuid === leg?.uuid}
                onClick={() => {
                  if (leg?.uuid) {
                    lineHaulMutation.mutate({
                      legUuid: leg.uuid,
                      isLineHaul: !isLegLineHaul(leg),
                    });
                    // Open AssignDriverCarrier modal with Line Haul labels
                    if (!isLegLineHaul(leg)) {
                      setSelectedLegForAssignment(leg);
                      setIsLineHaulModal(true);
                      setIsAssignCarrierModalOpen(true);
                    }
                  }
                }}
                startIcon={
                  pendingLineHaulLegUuid === leg?.uuid ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                {isLegLineHaul(leg)
                  ? 'Remove from Line Haul'
                  : 'Add to Line Haul'}
              </Button>
            )}
            <Button
              size="small"
              color="primary"
              onClick={() => {
                setSelectedLegForEquipment(leg);
                setIsAddEquipmentModalOpen(true);
              }}
            >
              + Add Equipment
            </Button>
          </>
        ) : (
          <>
            {/* When leg does NOT have manifest: Show Assign/Add buttons */}
            {/* Disable if this is not the pickup leg AND pickup leg is not delivered */}
            {canAssign && (
              <Button
                size="small"
                color="primary"
                disabled={!canAssign}
                onClick={() => {
                  if (canAssign) {
                    setSelectedLegForAssignment?.(leg);
                    setIsAssignCarrierModalOpen(true);
                    handleMenuClose();
                  }
                }}
              >
                Assign carrier/driver
              </Button>
            )}
            {canAssign && (
              <Button
                size="small"
                color="primary"
                disabled={!canAssign}
                onClick={() => {
                  if (canAssign) {
                    setSelectedLegForManifest(leg);
                    setIsAddToExistingManifestModalOpen(true);
                  }
                }}
              >
                Add To Existing Manifest
              </Button>
            )}

            <Button
              size="small"
              color="primary"
              onClick={() => handleOpenSplitLegModal?.(leg)}
            >
              Split Leg
            </Button>
            {canAssignWarehouseLineHaulButton && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={isAssigningWarehouseLineHaul}
                onClick={() => {
                  if (leg?.uuid) {
                    assignToLineHaulMutation.mutate(leg);
                  }
                }}
                startIcon={
                  isAssigningWarehouseLineHaul ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {isAssigningWarehouseLineHaul
                  ? 'Assigning...'
                  : 'Assign to Line Haul'}
              </Button>
            )}
            {canSetLineHaulButton && (
              <Button
                size="small"
                disabled={pendingLineHaulLegUuid === leg?.uuid}
                onClick={() => {
                  if (leg?.uuid) {
                    lineHaulMutation.mutate({
                      legUuid: leg.uuid,
                      isLineHaul: !isLegLineHaul(leg),
                    });
                    // Open AssignDriverCarrier modal with Line Haul labels
                    if (!isLegLineHaul(leg)) {
                      setSelectedLegForAssignment(leg);
                      setIsLineHaulModal(true);
                      setIsAssignCarrierModalOpen(true);
                    }
                  }
                }}
                startIcon={
                  pendingLineHaulLegUuid === leg?.uuid ? (
                    <CircularProgress size={16} />
                  ) : null
                }
              >
                {isLegLineHaul(leg)
                  ? 'Remove from Line Haul'
                  : 'Add to Line Haul'}
              </Button>
            )}
            <Button
              size="small"
              color="primary"
              onClick={() => {
                setSelectedLegForEquipment(leg);
                setIsAddEquipmentModalOpen(true);
              }}
            >
              + Add Equipment
            </Button>
          </>
        )}

        {/* Delete Leg button - always available when applicable */}
        {shouldShowDeleteLeg(sortedLegs, index) && (
          <Button
            size="small"
            color="error"
            onClick={() => {
              handleDeleteLeg(leg, index);
            }}
          >
            Delete Leg
          </Button>
        )}
      </Box>
    );
  };

  const LegRow = ({
    leg,
    index,
    setSelectedLegForAssignment,
    setIsAssignCarrierModalOpen,
    handleMenuClose,
    onMarkAsPickedUp,
    onMarkAsDelivered,
    canLegBeAssigned,
    canSetLineHaul,
    canAssignWarehouseLineHaul,
    firstPickupLeg,
  }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const canAssignWarehouseLineHaulButton = canAssignWarehouseLineHaul(leg);
    const isAssigningWarehouseLineHaul =
      assignToLineHaulMutation.isPending &&
      pendingLineHaulLegUuid === leg?.uuid;

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };
    const ITEM_HEIGHT = 48;
    return (
      <React.Fragment key={leg?.uuid || index}>
        <TableRow>
          <TableCell>
            <Chip
              label={leg?.status || 'N/A'}
              size="small"
              sx={{ bgcolor: '#e0e0e0' }}
            />
          </TableCell>
          <TableCell>
            {/* <Typography variant="body2">{leg?.type || 'N/A'}</Typography> */}
            <div className="font-12 font-bold flex items-center gap-1">
              P/D
              <svg
                width="12"
                height="12"
                viewBox="0 0 13 18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>compare_arrows - material</title>
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(-37 -4)" fill="#000">
                    <path
                      d="M46.1546392,13.8453608 L42.7319588,10.4226804 L46.1546392,7 L46.1546392,9.57731959 L52.1546392,9.57731959 L52.1546392,11.2680412 L46.1546392,11.2680412 L46.1546392,13.8453608 Z M41,14.7319588 L41,12.1546392 L44.4226804,15.5773196 L41,19 L41,16.4226804 L35,16.4226804 L35,14.7319588 L41,14.7319588 Z"
                      transform="translate(43.57732 13) scale(1 -1) rotate(-90) translate(-43.57732 -13)"
                    />
                  </g>
                </g>
              </svg>
            </div>

            {isLegLineHaul(leg) && (
              <Chip
                label="Line Haul"
                size="small"
                color="secondary"
                sx={{ mt: 0.5 }}
              />
            )}
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {leg?.origin?.name ||
                leg?.origin?.companyName ||
                leg?.destination?.companyName ||
                'N/A'}
            </Typography>
            <Typography variant="body2">
              {leg?.origin?.address || 'N/A'}
            </Typography>
            <Typography variant="caption">
              {(() => {
                const pickupStart = formatDate?.(leg?.pickUpTimeStart);
                const pickupEnd = formatDate?.(leg?.pickUpTimeEnd);
                if (pickupStart && pickupEnd) {
                  return `${pickupStart} - ${pickupEnd}`;
                } else if (pickupEnd) {
                  return pickupEnd;
                } else if (pickupStart) {
                  return pickupStart;
                }
                return 'N/A';
              })()}
            </Typography>
          </TableCell>
          <TableCell>
            <MoveRight fontSize="small" sx={{ color: 'primary.main' }} />
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {leg?.destination?.name || 'N/A'}
            </Typography>
            <Typography variant="body2">
              {leg?.destination?.address || 'N/A'}
            </Typography>
            <Typography variant="caption">
              {(() => {
                const deliveryStart = formatDate?.(leg?.deliveryTimeStart);
                const deliveryEnd = formatDate?.(leg?.deliveryTimeEnd);
                if (deliveryStart && deliveryEnd) {
                  return `${deliveryStart} - ${deliveryEnd}`;
                } else if (deliveryEnd) {
                  return deliveryEnd;
                } else if (deliveryStart) {
                  return deliveryStart;
                }
                return 'N/A';
              })()}
            </Typography>
          </TableCell>
          <TableCell>
            {(() => {
              const legManifest = leg?.manifest || leg?.manifestStop?.manifest;
              const manifestUuid = legManifest?.uuid || leg?.manifestUuid;
              const manifestName =
                legManifest?.manifestName ||
                leg?.manifestName ||
                legManifest?.manifestNumber ||
                leg?.manifestNumber;

              if (manifestUuid || manifestName) {
                return (
                  <Link
                    href={`/manifest/blank/${manifestUuid}`}
                    sx={{
                      color: '#0F3B95',
                      fontWeight: 'bold',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {manifestName || 'Manifest'}
                  </Link>
                );
              }
              return (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              );
            })()}
          </TableCell>
          <TableCell>
            <Typography
              variant="body2"
              color="error.main"
              sx={{ fontWeight: 'bold', mb: 0.5 }}
            >
              - $ {leg?.costs?.toFixed(2) || '0.00'}
            </Typography>
            <Typography
              sx={{ fontWeight: 'bold' }}
              variant="body2"
              color="success.main"
            >
              + $ {leg?.margin?.toFixed(2) || '0.00'}
            </Typography>
          </TableCell>
          <TableCell sx={{ position: 'relative' }}>
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={open ? 'long-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
              onClick={handleClick}
              disabled={isOrderLocked}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: ITEM_HEIGHT * 4.5,
                    width: '20ch',
                  },
                },
                list: {
                  'aria-labelledby': 'long-button',
                },
              }}
            >
              {/* Check if leg has a manifest */}
              {(() => {
                const legManifest =
                  leg?.manifest || leg?.manifestStop?.manifest;
                const manifestUuid = legManifest?.uuid || leg?.manifestUuid;
                const hasManifest = !!(
                  legManifest?.manifestName ||
                  legManifest?.manifestNumber ||
                  manifestUuid ||
                  leg?.manifestId
                );
                return (
                  <>
                    {/* Show Assign Carrier/Driver - disabled if leg has manifest or can't be assigned */}
                    {!hasManifest && (
                      <MenuItem
                        disabled={!canLegBeAssigned(leg)}
                        title={
                          !canLegBeAssigned(leg)
                            ? leg?.uuid === firstPickupLeg?.uuid
                              ? ''
                              : 'The pickup leg must be delivered before assigning driver/carrier to this leg'
                            : ''
                        }
                        onClick={() => {
                          if (canLegBeAssigned(leg)) {
                            setSelectedLegForAssignment(leg);
                            setIsAssignCarrierModalOpen(true);
                            handleMenuClose();
                          }
                        }}
                      >
                        Assign Carrier/Driver
                      </MenuItem>
                    )}

                    {/* Show Add To Existing Manifest - disabled if leg has manifest or can't be assigned */}
                    {!hasManifest && (
                      <MenuItem
                        disabled={!canLegBeAssigned(leg)}
                        title={
                          !canLegBeAssigned(leg)
                            ? leg?.uuid === firstPickupLeg?.uuid
                              ? ''
                              : 'The pickup leg must be delivered before adding this leg to manifest'
                            : ''
                        }
                        onClick={() => {
                          if (canLegBeAssigned(leg)) {
                            onOpenAddToManifestModal?.(leg);
                            setSelectedLegForManifest(leg);
                            setIsAddToExistingManifestModalOpen(true);
                            handleMenuClose();
                          }
                        }}
                      >
                        Add To Existing Manifest
                      </MenuItem>
                    )}

                    {/* Show Remove from Manifest when leg has a manifest */}
                    {hasManifest && (
                      <MenuItem
                        disabled={
                          !['attempted-pickup', 'attempted-delivery'].includes(
                            leg?.status?.toLowerCase(),
                          )
                        }
                        title="Only attempted-pickup or attempted-delivery legs can be removed from manifest"
                        onClick={() => {
                          if (
                            !['attempted-pickup', 'attempted-delivery'].includes(
                              leg?.status?.toLowerCase(),
                            )
                          ) {
                            return;
                          }
                          setSelectedLegForManifest(leg);
                          removeLegFromManifestMutation.mutate({
                            manifestUuid: manifestUuid,
                            legUuid: leg.uuid,
                          });
                          handleMenuClose();
                        }}
                      >
                        Remove from Manifest
                      </MenuItem>
                    )}
                  </>
                );
              })()}

              {canAssignWarehouseLineHaulButton && (
                <MenuItem
                  disabled={isAssigningWarehouseLineHaul}
                  onClick={() => {
                    if (leg?.uuid) {
                      assignToLineHaulMutation.mutate(leg);
                    }
                    handleClose();
                  }}
                >
                  {isAssigningWarehouseLineHaul ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Assigning...
                    </>
                  ) : (
                    'Assign to Line Haul'
                  )}
                </MenuItem>
              )}

              {canSetLineHaul(leg) && (
                <MenuItem
                  disabled={pendingLineHaulLegUuid === leg?.uuid}
                  onClick={() => {
                    if (leg?.uuid) {
                      lineHaulMutation.mutate({
                        legUuid: leg.uuid,
                        isLineHaul: !isLegLineHaul(leg),
                      });
                    }
                    handleClose();
                  }}
                >
                  {pendingLineHaulLegUuid === leg?.uuid ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      {isLegLineHaul(leg) ? 'Unsetting...' : 'Setting...'}
                    </>
                  ) : isLegLineHaul(leg) ? (
                    'Remove from Line Haul'
                  ) : (
                    'Add to Line Haul'
                  )}
                </MenuItem>
              )}

              <MenuItem
                onClick={() => {
                  setSelectedLegForTimes(leg);
                  setIsChangeTimesModalOpen(true);
                  handleMenuClose();
                }}
              >
                Change Times
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setSelectedLegForEquipment(leg);
                  setIsAddEquipmentModalOpen(true);
                  handleMenuClose();
                }}
              >
                Add Equipment
              </MenuItem>

              {leg.status?.toLowerCase() === 'picked' && (
                <MenuItem
                  onClick={() => {
                    handleUndoPicked(leg);
                    handleMenuClose();
                  }}
                  disabled={undoPickedMutation.isPending}
                >
                  Undo Picked
                </MenuItem>
              )}

              {leg.status?.toLowerCase() === 'delivered' && (
                <MenuItem
                  onClick={() => {
                    handleUndoDelivered(leg);
                    handleMenuClose();
                  }}
                  disabled={undoDeliveredMutation.isPending}
                >
                  Undo Delivered
                </MenuItem>
              )}

              {shouldShowDeleteLeg(sortedLegs, index) && (
                <MenuItem
                  onClick={() => {
                    handleDeleteLeg(leg, index);
                    handleMenuClose();
                  }}
                >
                  Delete Leg
                </MenuItem>
              )}
            </Menu>
          </TableCell>
        </TableRow>

        <TableRow sx={{ backgroundColor: '#fff' }}>
          <TableCell colSpan={8} sx={{ py: 1 }}>
            <LegActions
              leg={leg}
              index={index}
              onMarkAsPickedUp={onMarkAsPickedUp}
              onMarkAsDelivered={onMarkAsDelivered}
              canLegBeAssigned={canLegBeAssigned}
              canSetLineHaul={canSetLineHaul}
              canAssignWarehouseLineHaul={canAssignWarehouseLineHaul}
              firstPickupLeg={firstPickupLeg}
            />
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const FinancialSummary = () => (
    <Grid
      container
      spacing={0}
      sx={{ mt: 2, width: '30%', marginLeft: 'auto' }}
    >
      {totals.revenue > 0 && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total Revenue (CAD)
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              $ {totals.revenue.toFixed(2)}
            </Typography>
          </Box>
        </Grid>
      )}

      {totals.costs > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1,
              color: 'error.main',
            }}
          >
            <Typography variant="body2">Total Costs (CAD)</Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              - $ {totals.costs.toFixed(2)}
            </Typography>
          </Box>
        </Grid>
      )}

      {selectedQuote?.totalDiscount > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1,
              color: 'error.main',
            }}
          >
            <Typography variant="body2">
              Total Discount {selectedQuote?.discountType}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              - {selectedQuote?.discountType}{' '}
              {selectedQuote?.totalDiscount?.toFixed(2)}
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box sx={{ px: '16px !important' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          my: 1,
        }}
      >
        <Typography variant="h6">
          Legs {sortedLegs.length > 0 && `(${sortedLegs.length})`}
        </Typography>

        {sortedLegs.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsAllocatedRevenueModalOpen(true)}
            disabled={isOrderLocked}
          >
            Edit Allocated Revenue
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ position: 'relative' }}>
        {isAnyLegMutationLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Updating leg table...
              </Typography>
            </Box>
          </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Origin</TableCell>
              <TableCell></TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Manifest</TableCell>
              <TableCell>Est. Margin</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedLegs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No legs available for this order
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedLegs.map((leg, index) => (
                <LegRow
                  key={leg?.uuid || `leg-${index}`}
                  leg={leg}
                  index={index}
                  setSelectedLegForAssignment={setSelectedLegForAssignment}
                  setIsAssignCarrierModalOpen={setIsAssignCarrierModalOpen}
                  handleMenuClose={handleMenuClose}
                  onMarkAsPickedUp={handleMarkAsPickedUp}
                  onMarkAsDelivered={handleMarkAsDelivered}
                  canLegBeAssigned={canLegBeAssigned}
                  canSetLineHaul={canSetLineHaul}
                  canAssignWarehouseLineHaul={canAssignWarehouseLineHaul}
                  firstPickupLeg={firstPickupLeg}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <FinancialSummary />

      <SplitLegModal
        open={isSplitLegModalOpen}
        handleClose={handleCloseSplitLegModal}
        handleSubmit={handleSplitLegSubmit}
        isSubmitting={
          splitLegMutation.isPending || splitLegMutation.isLoading
        }
      />
      <AssignDriverCarrier
        open={isAssignCarrierModalOpen}
        handleClose={() => {
          setIsAssignCarrierModalOpen(false);
          setIsLineHaulModal(false);
        }}
        leg={selectedLegForAssignment}
        isOSND={isOSND}
        isLineHaul={isLineHaulModal}
        refetchOrders={refetchOrders}
      />
      <AddEquipmentModal
        open={isAddEquipmentModalOpen}
        handleClose={() => setIsAddEquipmentModalOpen(false)}
        leg={selectedLegForEquipment}
        isSaving={
          assignEquipmentMutation.isPending || assignEquipmentMutation.isLoading
        }
        onAssign={(legUuid, { toAdd = [], toRemove = [], selectedUuids = [] } = {}) => {
          if (!legUuid) {
            toast.error('No leg selected for equipment update.');
            return;
          }
          if (toAdd.length === 0 && toRemove.length === 0) {
            toast.error('No equipment changes to save.');
            return;
          }
          assignEquipmentMutation.mutate({
            legUuid,
            equipmentUuids: selectedUuids,
            toAdd,
            toRemove,
          });
        }}
      />
      <AddToExistingManifestModal
        open={isAddToExistingManifestModalOpen}
        handleClose={() => setIsAddToExistingManifestModalOpen(false)}
        // Modal expects an array of legs; pass the selected leg if present
        legs={selectedLegForManifest ? [selectedLegForManifest] : []}
        refetchOrders={refetchOrders}
        isOSND={isOSND}
      />

      <EditAllocatedRevenue
        data={{}}
        order={order}
        // Include all legs; EditAllocatedRevenue will internally treat Line Haul legs as fixed 0
        legs={sortedLegs}
        totalRevenue={allocatedRevenueTotal}
        orderUuid={order?.uuid}
        openAllocated={isAllocatedRevenueModalOpen}
        handleCloseAllocated={() => setIsAllocatedRevenueModalOpen(false)}
        onSave={(payload) => saveAllocatedRevenueMutation.mutateAsync(payload)}
      />
      <ChangeTimesModal
        open={isChangeTimesModalOpen}
        handleClose={() => {
          setIsChangeTimesModalOpen(false);
          setSelectedLegForTimes(null);
        }}
        leg={selectedLegForTimes}
        refetchOrders={refetchOrders}
      />
    </Box>
  );
};

export default LegsTable;
